import { css } from 'styled-components';

import { upperCaseFirst } from '../helper/upper-case-first';

export type ColorShade = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

export type ColorPrimarySimple = 'green' | 'blue' | 'purple' | 'red' | 'orange';
export type ColorDarkSimple = 'dark';
export type ColorSnackSimple =
    | 'success'
    | 'info'
    | 'error'
    | 'warning1'
    | 'warning2';

export type ColorOpacity5 =
    | 'nectGreenOpacity5'
    | 'primaryBlueOpacity5'
    | 'primaryPurpleOpacity5'
    | 'primaryRedOpacity5'
    | 'primaryOrangeOpacity5';
export type ColorPrimary100 =
    | 'nectGreen100'
    | 'primaryBlue100'
    | 'primaryPurple100'
    | 'primaryRed100'
    | 'primaryOrange100';
export type ColorPrimary200 =
    | 'nectGreen200'
    | 'primaryBlue200'
    | 'primaryPurple200'
    | 'primaryRed200'
    | 'primaryOrange200';
export type ColorPrimary300 =
    | 'nectGreen300'
    | 'primaryBlue300'
    | 'primaryPurple300'
    | 'primaryRed300'
    | 'primaryOrange300';
export type ColorPrimary400 =
    | 'nectGreen400'
    | 'primaryBlue400'
    | 'primaryPurple400'
    | 'primaryRed400'
    | 'primaryOrange400';
export type ColorPrimary500 =
    | 'nectGreen500'
    | 'primaryBlue500'
    | 'primaryPurple500'
    | 'primaryRed500'
    | 'primaryOrange500';
export type ColorPrimary600 =
    | 'nectGreen600'
    | 'primaryBlue600'
    | 'primaryPurple600'
    | 'primaryRed600'
    | 'primaryOrange600';
export type ColorPrimary700 =
    | 'nectGreen700'
    | 'primaryBlue700'
    | 'primaryPurple700'
    | 'primaryRed700'
    | 'primaryOrange700';
export type ColorPrimary800 =
    | 'nectGreen800'
    | 'primaryBlue800'
    | 'primaryPurple800'
    | 'primaryRed800'
    | 'primaryOrange800';
export type ColorPrimary900 =
    | 'nectGreen900'
    | 'primaryBlue900'
    | 'primaryPurple900'
    | 'primaryRed900'
    | 'primaryOrange900';

export type ColorPrimary =
    | ColorPrimary100
    | ColorPrimary200
    | ColorPrimary300
    | ColorPrimary400
    | ColorPrimary500
    | ColorPrimary600
    | ColorPrimary700
    | ColorPrimary800
    | ColorPrimary900;

export type ColorSnackBase =
    | 'snackSuccessBase'
    | 'snackInfoBase'
    | 'snackErrorBase'
    | 'snackWarning1Base'
    | 'snackWarning2Base';

export type ColorSnackPad =
    | 'snackSuccessPad'
    | 'snackInfoPad'
    | 'snackErrorPad'
    | 'snackWarning1Pad'
    | 'snackWarning2Pad';

export type ForegroundColor = ColorPrimary500 | ColorSnackBase;
export type BackgroundColor = ColorPrimary | ColorSnackPad;
export type ColorSnackSuccess = 'snackSuccessPad' | 'snackSuccessBase';
export type ColorSnackInfo = 'snackInfoPad' | 'snackInfoBase';
export type ColorSnackError = 'snackErrorPad' | 'snackErrorBase';
export type ColorSnackWarning1 = 'snackWarning1Pad' | 'snackWarning1Base';
export type ColorSnackWarning2 = 'snackWarning2Pad' | 'snackWarning2Base';

export type ColorSnack =
    | ColorSnackSuccess
    | ColorSnackInfo
    | ColorSnackError
    | ColorSnackWarning1
    | ColorSnackWarning2;

// eslint-disable-next-line no-shadow
export enum Color {
    /**
     *
     * Primary Colors
     *
     */

    // Nect Green

    /** @color Nect Green / 5% */
    nectGreenOpacity5 = 'Nect Green 5%',

    /** @color Nect Green / 100 Tint */
    nectGreen100 = 'Nect Green 100 Tint',
    /** @color Nect Green / 200 Tint */
    nectGreen200 = 'Nect Green 200 Tint',
    /** @color Nect Green / 300 Tint */
    nectGreen300 = 'Nect Green 300 Tint',
    /** @color Nect Green / 400 Tint */
    nectGreen400 = 'Nect Green 400 Tint',

    /** @color Nect Green / 500 Base */
    nectGreen500 = 'Nect Green 500 Base',

    /** @color Nect Green / 600 Shade */
    nectGreen600 = 'Nect Green 600 Shade',
    /** @color Nect Green / 700 Shade */
    nectGreen700 = 'Nect Green 700 Shade',
    /** @color Nect Green / 800 Shade */
    nectGreen800 = 'Nect Green 800 Shade',
    /** @color Nect Green / 900 Shade */
    nectGreen900 = 'Nect Green 900 Shade',

    // Primary Blue
    /** @color Primary Blue / 5% */
    primaryBlueOpacity5 = 'Primary Blue 5%',

    /** @color Primary Blue / 100 Tint */
    primaryBlue100 = 'Primary Blue 100 Tint',
    /** @color Primary Blue / 200 Tint */
    primaryBlue200 = 'Primary Blue 200 Tint',
    /** @color Primary Blue / 300 Tint */
    primaryBlue300 = 'Primary Blue 300 Tint',
    /** @color Primary Blue / 400 Tint */
    primaryBlue400 = 'Primary Blue 400 Tint',

    /** @color Primary Blue / 500 Base */
    primaryBlue500 = 'Primary Blue 500 Base',

    /** @color Primary Blue / 600 Shade */
    primaryBlue600 = 'Primary Blue 600 Shade',
    /** @color Primary Blue / 700 Shade */
    primaryBlue700 = 'Primary Blue 700 Shade',
    /** @color Primary Blue / 800 Shade */
    primaryBlue800 = 'Primary Blue 800 Shade',
    /** @color Primary Blue / 900 Shade */
    primaryBlue900 = 'Primary Blue 900 Shade',

    // Primary Purple
    /** @color Primary Purple / 5% */
    primaryPurpleOpacity5 = 'Primary Purple 5%',

    /** @color Primary Purple / 100 Tint */
    primaryPurple100 = 'Primary Purple 100 Tint',
    /** @color Primary Purple / 200 Tint */
    primaryPurple200 = 'Primary Purple 200 Tint',
    /** @color Primary Purple / 300 Tint */
    primaryPurple300 = 'Primary Purple 300 Tint',
    /** @color Primary Purple / 400 Tint */
    primaryPurple400 = 'Primary Purple 400 Tint',

    /** @color Primary Purple / 500 Base */
    primaryPurple500 = 'Primary Purple 500 Base',

    /** @color Primary Purple / 600 Shade */
    primaryPurple600 = 'Primary Purple 600 Shade',
    /** @color Primary Purple / 700 Shade */
    primaryPurple700 = 'Primary Purple 700 Shade',
    /** @color Primary Purple / 800 Shade */
    primaryPurple800 = 'Primary Purple 800 Shade',
    /** @color Primary Purple / 900 Shade */
    primaryPurple900 = 'Primary Purple 900 Shade',

    // Primary Red
    /** @color Primary Red / 5% */
    primaryRedOpacity5 = 'Primary Red 5%',

    /** @color Primary Red / 100 Tint */
    primaryRed100 = 'Primary Red 100 Tint',
    /** @color Primary Red / 200 Tint */
    primaryRed200 = 'Primary Red 200 Tint',
    /** @color Primary Red / 300 Tint */
    primaryRed300 = 'Primary Red 300 Tint',
    /** @color Primary Red / 400 Tint */
    primaryRed400 = 'Primary Red 400 Tint',

    /** @color Primary Red / 500 Base */
    primaryRed500 = 'Primary Red 500 Base',

    /** @color Primary Red / 600 Shade */
    primaryRed600 = 'Primary Red 600 Shade',
    /** @color Primary Red / 700 Shade */
    primaryRed700 = 'Primary Red 700 Shade',
    /** @color Primary Red / 800 Shade */
    primaryRed800 = 'Primary Red 800 Shade',
    /** @color Primary Red / 900 Shade */
    primaryRed900 = 'Primary Red 900 Shade',

    // Primary Orange
    /** @color Primary Orange / 5% */
    primaryOrangeOpacity5 = 'Primary Orange 5%',

    /** @color Primary Orange / 100 Tint */
    primaryOrange100 = 'Primary Orange 100 Tint',
    /** @color Primary Orange / 200 Tint */
    primaryOrange200 = 'Primary Orange 200 Tint',
    /** @color Primary Orange / 300 Tint */
    primaryOrange300 = 'Primary Orange 300 Tint',
    /** @color Primary Orange / 400 Tint */
    primaryOrange400 = 'Primary Orange 400 Tint',

    /** @color Primary Orange / 500 Base */
    primaryOrange500 = 'Primary Orange 500 Base',

    /** @color Primary Orange / 600 Shade */
    primaryOrange600 = 'Primary Orange 600 Shade',
    /** @color Primary Orange / 700 Shade */
    primaryOrange700 = 'Primary Orange 700 Shade',
    /** @color Primary Orange / 800 Shade */
    primaryOrange800 = 'Primary Orange 800 Shade',
    /** @color Primary Orange / 900 Shade */
    primaryOrange900 = 'Primary Orange 900 Shade',

    /**
     *
     * Greys
     *
     */
    /** @color Grey / 100 */
    grey100 = 'Grey 100',
    /** @color Grey / 200 */
    grey200 = 'Grey 200',
    /** @color Grey / 300 */
    grey300 = 'Grey 300',
    /** @color Grey / 400 */
    grey400 = 'Grey 400',
    /** @color Grey / 500 */
    grey500 = 'Grey 500',
    /** @color Grey / 600 */
    grey600 = 'Grey 600',
    /** @color Grey / 700 */
    grey700 = 'Grey 700',

    /**
     *
     * Whites
     *
     */
    /** @color White / 100% */
    white100 = 'White 100%',
    /** @color White / 90% */
    white90 = 'White 90%',
    /** @color White / 80% */
    white80 = 'White 80%',
    /** @color White / 70% */
    white70 = 'White 70%',
    /** @color White / 60% */
    white60 = 'White 60%',
    /** @color White / 50% */
    white50 = 'White 50%',
    /** @color White / 40% */
    white40 = 'White 40%',
    /** @color White / 30% */
    white30 = 'White 30%',
    /** @color White / 20% */
    white20 = 'White 20%',
    /** @color White / 10% */
    white10 = 'White 10%',
    /** @color White / 5% */
    white5 = 'White 5%',
    /** @color White / 1% */
    white1 = 'White 1%',

    /**
     *
     * Blacks
     *
     */
    /** @color Black / 100% */
    black100 = 'Black 100%',
    /** @color Black / 90% */
    black90 = 'Black 90%',
    /** @color Black / 80% */
    black80 = 'Black 80%',
    /** @color Black / 70% */
    black70 = 'Black 70%',
    /** @color Black / 60% */
    black60 = 'Black 60%',
    /** @color Black / 50% */
    black50 = 'Black 50%',
    /** @color Black / 40% */
    black40 = 'Black 40%',
    /** @color Black / 30% */
    black30 = 'Black 30%',
    /** @color Black / 20% */
    black20 = 'Black 20%',
    /** @color Black / 10% */
    black10 = 'Black 10%',
    /** @color Black / 5% */
    black5 = 'Black 5%',
    /** @color Black / 1% */
    black1 = 'Black 1%',

    /**
     *
     * Others
     *
     */
    /** @color Other / Pearl */
    otherPearl = 'Other Pearl',
    /** @color Other / Apple */
    otherApple = 'Other Apple',
    /** @color Other / Lilac */
    otherLilac = 'Other Lilac',
    /** @color Other / Mauve */
    otherMauve = 'Other Mauve',
    /** @color Other / Milk Tea */
    otherMilkTea = 'Other Milk Tea',
    /** @color Other / Honey */
    otherHoney = 'Other Honey',

    /**
     *
     * Snacks
     *
     */
    /** @color Snack / Success Pad */
    snackSuccessPad = 'Snack Success Pad',
    /** @color Snack / Success Base */
    snackSuccessBase = 'Snack Success Base',

    /** @color Snack / Info Pad */
    snackInfoPad = 'Snack Info Pad',
    /** @color Snack / Info Base */
    snackInfoBase = 'Snack Info Base',

    /** @color Snack / Error Pad */
    snackErrorPad = 'Snack Error Pad',
    /** @color Snack / Error Base */
    snackErrorBase = 'Snack Error Base',

    /** @color Snack / Warning 1 Pad */
    snackWarning1Pad = 'Snack Warning 1 Pad',
    /** @color Snack / Warning 1 Base */
    snackWarning1Base = 'Snack Warning 1 Base',

    /** @color Snack / Warning 2 Pad */
    snackWarning2Pad = 'Snack Warning 2 Pad',
    /** @color Snack / Warning 2 Base */
    snackWarning2Base = 'Snack Warning 2 Base',

    // TBD Gradients
}

export const ColorStorybookPicker = {
    control: {
        type: 'select',
        options: Object.values(Color),
    },
};

export const hexAToRGBA = (h: string) => {
    let r = '0';
    let g = '0';
    let b = '0';
    let a: string | number = 1;

    if (h.length === 5) {
        r = `0x${h[1]}${h[1]}`;
        g = `0x${h[2]}${h[2]}`;
        b = `0x${h[3]}${h[3]}`;
        a = `0x${h[4]}${h[4]}`;
    } else if (h.length === 9) {
        r = `0x${h[1]}${h[2]}`;
        g = `0x${h[3]}${h[4]}`;
        b = `0x${h[5]}${h[6]}`;
        a = `0x${h[7]}${h[8]}`;
    }
    a = +((a as number) / 255).toFixed(3);

    return `rgba(${+r},${+g},${+b},${a})`;
};

type RGB = {
    red: number;
    green: number;
    blue: number;
};

export const hexToRGBAString = (colorValue: string, opacity = 1): string => {
    return `rgba(${parseInt(colorValue.substr(0, 2), 16)},${parseInt(
        colorValue.substr(2, 2),
        16,
    )},${parseInt(colorValue.substr(4, 2), 16)},${opacity})`;
};

// From https://github.com/edelstone/tints-and-shades/blob/6cd92a04b654b8bffa5d7d7094cba1f300ee5f1e/scripts/tints-and-shades.js
const hexToRGB = (colorValue: string): RGB => {
    return {
        red: parseInt(colorValue.substr(0, 2), 16),
        green: parseInt(colorValue.substr(2, 2), 16),
        blue: parseInt(colorValue.substr(4, 2), 16),
    };
};

// pad a hexadecimal string with zeros if it needs it
function pad(number: string, length: number) {
    // eslint-disable-next-line prefer-template
    let str = '' + number;
    while (str.length < length) {
        str = `0${str}`;
    }
    return str;
}

// convert an integer to a 2-char hex string
// for sanity, round it and ensure it is between 0 and 255
// 43 => '2b'
function intToHex(rgbint: number) {
    return pad(Math.min(Math.max(Math.round(rgbint), 0), 255).toString(16), 2);
}

// convert one of our rgb color objects to a full hex color string
// { red: 80, green: 18, blue: 20 } => '501214'
function rgbToHex(rgb: RGB) {
    return intToHex(rgb.red) + intToHex(rgb.green) + intToHex(rgb.blue);
}

// shade one of our rgb color objects to a distance of i*10%
// ({ red: 80, green: 18, blue: 20 }, 1) => { red: 72, green: 16, blue: 18 }
const rgbShade = (rgb: RGB, i: number): RGB => {
    return {
        red: rgb.red * (1 - 0.1 * i),
        green: rgb.green * (1 - 0.1 * i),
        blue: rgb.blue * (1 - 0.1 * i),
    };
};

// tint one of our rgb color objects to a distance of i*10%
// ({ red: 80, green: 18, blue: 20 }, 1) => { red: 98, green: 42, blue: 44 }
function rgbTint(rgb: RGB, i: number) {
    return {
        red: rgb.red + (255 - rgb.red) * i * 0.1,
        green: rgb.green + (255 - rgb.green) * i * 0.1,
        blue: rgb.blue + (255 - rgb.blue) * i * 0.1,
    };
}

export const calculateShade = (colorValue: string, shade: ColorShade) => {
    const color = hexToRGB(colorValue);

    const map = {
        100: () => rgbTint(color, 9),
        200: () => rgbTint(color, 7),
        300: () => rgbTint(color, 5),
        400: () => rgbTint(color, 3),
        500: () => color,
        600: () => rgbShade(color, 1),
        700: () => rgbShade(color, 3),
        800: () => rgbShade(color, 5),
        900: () => rgbShade(color, 7),
    };

    return `#${rgbToHex(map[shade]())}`;
};

export const primaryToColorShade = (
    color: ColorPrimarySimple,
    shade: ColorShade = 500,
) =>
    color === 'green'
        ? Color[`nectGreen${shade}` as ColorPrimary]
        : Color[`primary${upperCaseFirst(color)}${shade}` as ColorPrimary];

export const snackToColorShade = (
    color: ColorSnackSimple,
    kind: 'base' | 'pad',
) =>
    Color[`snack${upperCaseFirst(color)}${upperCaseFirst(kind)}` as ColorSnack];

export const primaryToColor500AndBackground100 = (
    color: ColorPrimarySimple,
    important = false,
) => {
    const textColor = primaryToColorShade(color, 500);
    const backgroundColor = primaryToColorShade(color, 100);

    return css`
        color: ${({ theme }) =>
            theme.color[textColor] + (important ? '!important' : '')};
        background-color: ${({ theme }) =>
            theme.color[backgroundColor] + (important ? '!important' : '')};
    `;
};

export const simpleToPrimary = (
    color: ColorPrimarySimple | ColorSnackSimple | ColorDarkSimple,
) => {
    const colorMap = {
        green: 'nectGreen500',
        blue: 'primaryBlue500',
        purple: 'primaryPurple500',
        red: 'primaryRed500',
        orange: 'primaryOrange500',
        dark: 'grey700',
        success: 'snackSuccessBase',
        info: 'snackInfoBase',
        error: 'snackErrorBase',
        warning1: 'snackWarning1Base',
        warning2: 'snackWarning2Base',
    };

    return Color[colorMap[color] as ForegroundColor];
};

export const simpleToShade = (
    color: ColorPrimarySimple | ColorSnackSimple | ColorDarkSimple,
    shade: ColorShade = 100,
) => {
    const colorMap = {
        green: `nectGreen${shade}`,
        blue: `primaryBlue${shade}`,
        purple: `primaryPurple${shade}`,
        red: `primaryRed${shade}`,
        orange: `primaryOrange${shade}`,
        dark: `grey${shade >= 700 ? 700 : shade}`,
        success: 'snackSuccessPad',
        info: 'snackInfoPad',
        error: 'snackErrorPad',
        warning1: 'snackWarning1Pad',
        warning2: 'snackWarning2Pad',
    };

    return Color[colorMap[color] as BackgroundColor];
};

export const simpleToOpacity5 = (
    color: ColorPrimarySimple | ColorSnackSimple | ColorDarkSimple,
) => {
    const colorMap = {
        green: 'nectGreenOpacity5',
        blue: 'primaryBlueOpacity5',
        purple: 'primaryPurpleOpacity5',
        red: 'primaryRedOpacity5',
        orange: 'primaryOrangeOpacity5',
        dark: 'black5',
        success: 'nectGreenOpacity5',
        info: 'primaryBlueOpacity5',
        error: 'primaryRedOpacity5',
        warning1: 'primaryOrangeOpacity5',
        warning2: 'primaryPurpleOpacity5',
    };

    return Color[colorMap[color] as ColorOpacity5];
};
